import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiSearch, FiChevronDown, FiX } from "react-icons/fi";

import "./gce-o.css";
import { SchoolService } from "../../services/school.service";
import { ReportsService } from "../../services/reports.service";
import { User, SchoolWithExamRegistration, ExamType } from "../../types/types";
import SchoolExamReportContent from "../../components/reports/SchoolExamReportContent";

interface School {
  id: number;
  name: string;
  email: string;
  region: string;
  division: string;
  sub_division: string;
  contact: string;
  school_admin: User;
  school_admin_id: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  exam_registrations: ExamRegistration[];
}

interface ExamRegistration {
  id: number;
  school_id: number;
  exam_session_id: number;
  exam_type_id: number;
  total_students: number;
  school_code: string;
  total_fee: string;
  discount_applied: string;
  payment_status: string;
  created_at: string;
  updated_at: string;
  examType: ExamType;
  payment: Payment | null;
  subject_registrations: {
    id: number;
    exam_registration_id: number;
    subject_id: number;
    student_count: number;
    created_at: string;
    updated_at: string;
  }[];
}

interface Payment {
  id: number;
  approved_by: number | null;
  exam_registration_id: number;
  amount: string;
  payment_method: string;
  transaction_id: string;
  payment_status: string;
  payment_attempts: number;
  refund_status: string;
  refund_amount: string;
  created_at: string;
  updated_at: string;
}

const ExamReceipts = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [selectedExamTypeIds, setSelectedExamTypeIds] = useState<number[]>([]);
  const [registrationData, setRegistrationData] =
    useState<SchoolWithExamRegistration | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Filters
  const [registrationStatus, setRegistrationStatus] = useState<
    "paid" | "unpaid" | "incomplete" | "all"
  >("all");
  const [paymentStatus, setPaymentStatus] = useState<"paid" | "unpaid" | "all">(
    "all"
  );
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [schoolSearch, setSchoolSearch] = useState<string>("");
  const [isSchoolMenuOpen, setIsSchoolMenuOpen] = useState(false);
  const schoolMenuRef = useRef<HTMLDivElement>(null);

  // On mount, initialize filters from URL and fetch initial data
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const regStatus = params.get("registrationStatus") as
      | "paid"
      | "unpaid"
      | "incomplete"
      | "all";
    const payStatus = params.get("paymentStatus") as "paid" | "unpaid" | "all";
    const schoolIdParam = params.get("schoolId");
    const examTypeIdsParam = params.get("examTypeIds");

    if (regStatus) setRegistrationStatus(regStatus);
    if (payStatus) setPaymentStatus(payStatus);
    if (schoolIdParam) {
      const id = Number(schoolIdParam);
      if (!isNaN(id)) setSelectedSchoolId(id);
    }
    if (examTypeIdsParam) {
      const ids = examTypeIdsParam
        .split(",")
        .map((id) => Number(id))
        .filter((id) => !isNaN(id));
      setSelectedExamTypeIds(ids);
    }

    fetchInitialData();

    // Click away listener for searchable dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (schoolMenuRef.current && !schoolMenuRef.current.contains(event.target as Node)) {
        setIsSchoolMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch report data when filters change
  useEffect(() => {
    if (selectedSchoolId && selectedExamTypeIds.length > 0) {
      fetchSchoolReport();
    } else {
      setRegistrationData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSchoolId,
    selectedExamTypeIds,
    registrationStatus,
    paymentStatus,
  ]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("registrationStatus", registrationStatus);
    params.set("paymentStatus", paymentStatus);
    if (selectedSchoolId) {
      params.set("schoolId", String(selectedSchoolId));
    }
    if (selectedExamTypeIds.length > 0) {
      params.set("examTypeIds", selectedExamTypeIds.join(","));
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [
    registrationStatus,
    paymentStatus,
    selectedSchoolId,
    selectedExamTypeIds,
    navigate,
  ]);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all schools
      const { data } = await SchoolService.getAllSchools();

      setSchools(data || []);
    } catch (err) {
      setError("Failed to fetch initial data");
      console.error("Error fetching initial data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolReport = async () => {
    if (!selectedSchoolId || selectedExamTypeIds.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      const response = await ReportsService.getSchoolExamReports({
        exam_type_ids: selectedExamTypeIds,
        school_id: selectedSchoolId,
        registration_status: registrationStatus,
        payment_status: paymentStatus,
      });

      setRegistrationData(response as SchoolWithExamRegistration);
    } catch (err) {
      setError("Failed to fetch school report");
      console.error("Error fetching school report:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationStatusChange = (
    status: "paid" | "unpaid" | "incomplete" | "all"
  ) => {
    setRegistrationStatus(status);
  };

  const handlePaymentStatusChange = (status: "paid" | "unpaid" | "all") => {
    setPaymentStatus(status);
  };

  const handleSchoolChange = (schoolId: number | null) => {
    setSelectedSchoolId(schoolId);
    setSelectedExamTypeIds([]); // Reset exam types when school changes
    if (schoolId) {
      fetchExamTypesForSchool(schoolId);
    } else {
      setExamTypes([]);
    }
  };

  const handleExamTypeSelection = (examType: ExamType) => {
    const isSelected = selectedExamTypeIds.includes(examType.id);
    let newSelectedIds = [...selectedExamTypeIds];
    const groupKeywords = ["BEPC", "PROB", "BACC", "CAP", "TTT", "DIS"];

    if (isSelected) {
      // Remove the exam type
      newSelectedIds = newSelectedIds.filter((id) => id !== examType.id);

      // Auto-deselect related exam types
      // First, for groups
      for (const keyword of groupKeywords) {
        if (examType.name.includes(keyword)) {
          const groupExamTypes = examTypes.filter((et) =>
            et.name.includes(keyword)
          );
          newSelectedIds = newSelectedIds.filter(
            (id) => !groupExamTypes.some((et) => et.id === id)
          );
          break;
        }
      }
    } else {
      // Add the exam type
      newSelectedIds.push(examType.id);

      // Auto-select related exam types
      // First, for groups
      for (const keyword of groupKeywords) {
        if (examType.name.includes(keyword)) {
          const groupExamTypes = examTypes.filter(
            (et) => et.name.includes(keyword) && !newSelectedIds.includes(et.id)
          );
          newSelectedIds.push(...groupExamTypes.map((et) => et.id));
          break;
        }
      }
    }

    setSelectedExamTypeIds(newSelectedIds);
  };

  const fetchExamTypesForSchool = async (schoolId: number) => {
    try {
      const response = await ReportsService.getSchoolExamTypes(schoolId);
      setExamTypes(response.data || []);
    } catch (err) {
      console.error("Error fetching exam types for school:", err);
      setExamTypes([]);
    }
  };

  const downloadPDFBackend = async () => {
    if (selectedExamTypeIds.length === 0 || !selectedSchoolId) return;

    setIsDownloading(true);
    try {
      const blob = await ReportsService.downloadAdminExamReceiptPdf({
        exam_type_ids: selectedExamTypeIds,
        school_id: selectedSchoolId,
      });

      const fileName = `admin-exam-receipt-${selectedSchoolId}-${selectedExamTypeIds.join(",")}-${new Date().toISOString()}.pdf`;

      // Create a link element to trigger the download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Compute whether download button should be disabled
  const isDownloadDisabled =
    loading ||
    !selectedSchoolId ||
    selectedExamTypeIds.length === 0 ||
    registrationData?.exam_registration?.payment_status !== "paid";

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500">Error: {error}</div>;
  }

  return (
    <>
      {/* Controls */}
      <div className="report-controls mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Report Filters & Controls
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Registration Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Registration Status
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-4 py-2 rounded-md transition-colors ${registrationStatus === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handleRegistrationStatusChange("all")}
              >
                All
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${registrationStatus === "paid"
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handleRegistrationStatusChange("paid")}
              >
                Paid
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${registrationStatus === "unpaid"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handleRegistrationStatusChange("unpaid")}
              >
                Unpaid
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${registrationStatus === "incomplete"
                  ? "bg-red-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handleRegistrationStatusChange("incomplete")}
              >
                Incomplete
              </button>
            </div>
          </div>
          {/* Payment Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Status
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-4 py-2 rounded-md transition-colors ${paymentStatus === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handlePaymentStatusChange("all")}
              >
                All
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${paymentStatus === "paid"
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handlePaymentStatusChange("paid")}
              >
                Paid
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${paymentStatus === "unpaid"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                onClick={() => handlePaymentStatusChange("unpaid")}
              >
                Unpaid
              </button>
            </div>
          </div>
          {/* School Selector */}
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select School
            </label>
            <div className="relative" ref={schoolMenuRef}>
              <button
                type="button"
                onClick={() => setIsSchoolMenuOpen(!isSchoolMenuOpen)}
                className="flex items-center justify-between w-full px-3 py-2 border rounded-md dark:text-white bg-white dark:bg-gray-800 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm active:ring-2"
              >
                <span className="truncate">
                  {selectedSchoolId
                    ? schools.find((s) => s.id === selectedSchoolId)?.name
                    : "Select School"}
                </span>
                <FiChevronDown className={`ml-2 transform transition-transform ${isSchoolMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isSchoolMenuOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow-lg overflow-hidden flex flex-col max-h-[400px]">
                  <div className="p-2 border-b dark:border-gray-700">
                    <div className="relative">
                      <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search school..."
                        value={schoolSearch}
                        onChange={(e) => setSchoolSearch(e.target.value)}
                        className="w-full pl-8 pr-8 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                      {schoolSearch && (
                        <button
                          onClick={() => setSchoolSearch("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-y-auto">
                    {schools
                      ?.filter((school) =>
                        school.name.toLowerCase().includes(schoolSearch.toLowerCase())
                      )
                      .length > 0 ? (
                      schools
                        ?.filter((school) =>
                          school.name.toLowerCase().includes(schoolSearch.toLowerCase())
                        )
                        .map((school) => (
                          <div
                            key={school.id}
                            onClick={() => {
                              handleSchoolChange(school.id);
                              setIsSchoolMenuOpen(false);
                              setSchoolSearch("");
                            }}
                            className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 ${selectedSchoolId === school.id
                              ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-medium"
                              : "text-gray-700 dark:text-gray-200"
                              }`}
                          >
                            {school.name}
                          </div>
                        ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500 italic text-center">
                        No schools found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Exam Type Selector */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Exam Types
          </label>
          <div className="border flex flex-wrap gap-4 rounded-md dark:border-gray-600 p-3 max-h-40 overflow-y-auto bg-gray-50 dark:bg-gray-700">
            {examTypes.map((examType) => (
              <label
                key={examType.id}
                className="flex items-center mb-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={selectedExamTypeIds.includes(examType.id)}
                  onChange={() => handleExamTypeSelection(examType)}
                  className="mr-2 accent-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-200 text-sm">
                  {examType.name}
                </span>
              </label>
            ))}
          </div>
        </div>
        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-4 justify-end">
          <button
            onClick={() => {
              setRegistrationStatus("all");
              setPaymentStatus("all");
              setSelectedSchoolId(null);
              setSelectedExamTypeIds([]);
              setExamTypes([]);
              setRegistrationData(null);
              navigate({ search: "" }, { replace: true });
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
          {/* <button
            onClick={downloadPDF}
            disabled={!selectedSchoolId || selectedExamTypeIds.length === 0}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Print PDF (Browser)
          </button> */}
          <button
            onClick={downloadPDFBackend}
            disabled={isDownloadDisabled || isDownloading}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isDownloading ? "Downloading..." : "Download Receipt (PDF)"}
          </button>
        </div>
      </div>

      {/* Report Content */}
      <SchoolExamReportContent
        registrationData={registrationData}
        selectedExamTypes={examTypes.filter((et) =>
          selectedExamTypeIds.includes(et.id)
        )}
        isALSelected={examTypes.some(
          (et) =>
            selectedExamTypeIds.includes(et.id) &&
            (et.name.includes("GCE A/L") || et.name.includes("TVE A/L"))
        )}
        examTypes={examTypes}
        selectedExamTypeIds={selectedExamTypeIds}
      />
    </>
  );
};

export default ExamReceipts;
